/**
 * Recommendation Engine admin page (SmartImprove) — Feb 2026.
 *
 * Consulting-grade warehouse transformation recommendation templates
 * aligned to the canonical Business Process + Sub-Process taxonomy.
 *
 * The sidebar entry is still "Recommendation Engine" (per user request)
 * but the page is internally treated as "Transformation Recommendation
 * Templates" — i.e. a consulting playbook, not an AI template list.
 *
 * Architecture rule (enforced in the seed + admin UX):
 *    Recommendations are CONSULTING-FIRST, NOT product-first.
 *    SAP modules and PrimeS4/WarehouseX accelerators appear only as
 *    optional Solution Enablement Options under each recommendation.
 *
 * The backend collection (`smartimprove_recommendation_templates`) and
 * route slug (`/recommendation-templates`) are preserved for backward
 * compatibility with the deterministic recommendation engine.
 */
import { useEffect, useMemo, useState, useCallback } from "react";
import { toast } from "sonner";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Textarea } from "../../components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "../../components/ui/dialog";
import { Plus, Pencil, Trash2 } from "lucide-react";
import {
  recommendationTemplatesApi,
  operationalAreasApi,
  subProcessesApi,
} from "../../lib/smartimproveAdminApi";

// ----- Canonical option sets (consultant-facing) -----
const TRANSFORMATION_THEMES = [
  "RF Modernization",
  "Warehouse Standardization",
  "Process Harmonization",
  "Inventory Optimization",
  "Workforce Productivity",
  "Automation Enablement",
  "KPI & Visibility Improvements",
  "Digital Transformation",
  "Operational Stability",
  "Integration Reliability",
  "Scalability Readiness",
];

const BUSINESS_IMPACT_AREAS = [
  "Productivity",
  "Throughput",
  "Inventory Accuracy",
  "Operational Visibility",
  "Workforce Efficiency",
  "Automation Maturity",
  "Process Efficiency",
  "Support Reduction",
  "Training Reduction",
  "Standardization Value",
];

const COMPLEXITY = [
  { value: "low", label: "Low" },
  { value: "medium", label: "Medium" },
  { value: "high", label: "High" },
];

const ROLLOUT_WAVE = [
  { value: "", label: "—" },
  { value: "stabilization", label: "Wave 1 — Stabilization" },
  { value: "process_optimization", label: "Wave 2 — Process Optimization" },
  { value: "standardization", label: "Wave 3 — Standardization" },
  { value: "advanced_automation", label: "Wave 4 — Advanced Automation" },
];

const WAREHOUSE_MATURITY = [
  { value: "", label: "—" },
  { value: "Foundational", label: "Foundational" },
  { value: "Intermediate", label: "Intermediate" },
  { value: "Advanced", label: "Advanced" },
];

const SAP_STANDARD_OPTIONS = [
  "SAP EWM RF Framework",
  "SAP Fiori",
  "SAP Labor Management",
  "SAP MFS",
  "SAP Yard Logistics",
];
const PRIMES4_OPTIONS = [
  "SmartRF",
  "SmartInspect",
  "SmartAssist",
  "Supervisor AI Assist",
  "SmartCenter",
  "WarehouseX Analytics",
  "SmartEvaluate",
  "SmartImprove",
];
const FUTURE_OPTIONS = [
  "Voice picking",
  "AI exception handling",
  "Robotics integration",
  "Advanced warehouse automation",
  "Predictive replenishment",
];

const COMPLEXITY_PILL = {
  low: "bg-[#F1F5F9] text-[#475569] border-[#E2E8F0]",
  medium: "bg-[#EFF6FF] text-[#1D4ED8] border-[#BFDBFE]",
  high: "bg-[#FFF7ED] text-[#C2410C] border-[#FED7AA]",
};

const WAVE_PILL = {
  stabilization: "bg-[#FEE2E2] text-[#991B1B] border-[#FCA5A5]",
  process_optimization: "bg-[#EFF6FF] text-[#1D4ED8] border-[#BFDBFE]",
  standardization: "bg-[#F0F9FF] text-[#075985] border-[#BAE6FD]",
  advanced_automation: "bg-[#ECFDF5] text-[#065F46] border-[#A7F3D0]",
};

const WAVE_SHORT_LABEL = {
  stabilization: "Wave 1",
  process_optimization: "Wave 2",
  standardization: "Wave 3",
  advanced_automation: "Wave 4",
};

function slugify(name) {
  return String(name || "")
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "")
    .slice(0, 80);
}

const EMPTY = {
  id: null,
  slug: "",
  title: "",
  operational_area: "",
  sub_process_key: "",
  description: "",
  transformation_theme: "",
  business_impact_area: "",
  implementation_complexity: "medium",
  rollout_wave: "",
  warehouse_maturity_alignment: "",
  expected_business_impact: "",
  operational_benefit: "",
  executive_summary_snippet: "",
  implementation_approach: "",
  duration_min_weeks: "",
  duration_max_weeks: "",
  estimated_functional_effort_hours: "",
  estimated_technical_effort_hours: "",
  change_management_required: false,
  recommended_for_multi_site: false,
  sap_standard_enablement: [],
  primes4_accelerators: [],
  future_advanced_enablement: [],
  active: true,
  sort_order: 0,
};

/* Chip-style multi-select for solution enablement options. */
function ChipMultiSelect({ value = [], options, onChange, testId }) {
  const set = new Set(value || []);
  const toggle = (opt) => {
    const next = new Set(set);
    if (next.has(opt)) next.delete(opt);
    else next.add(opt);
    onChange(Array.from(next));
  };
  return (
    <div className="flex flex-wrap gap-1.5" data-testid={testId}>
      {options.map((opt) => {
        const active = set.has(opt);
        return (
          <button
            key={opt}
            type="button"
            onClick={() => toggle(opt)}
            className={`px-2 py-1 text-[11px] rounded-sm border transition ${
              active
                ? "bg-[#025979] text-white border-[#025979]"
                : "bg-white text-[#374151] border-[#E5E7EB] hover:border-[#94A3B8]"
            }`}
            data-testid={`${testId}-${slugify(opt)}`}
          >
            {opt}
          </button>
        );
      })}
    </div>
  );
}

function RecommendationDialog({
  open,
  onOpenChange,
  value,
  processes,
  subProcesses,
  onChange,
  onSave,
  saving,
}) {
  const isEdit = !!value?.id;
  const subOptions = useMemo(
    () =>
      (subProcesses || [])
        .filter(
          (s) =>
            s.active !== false && s.process_key === value?.operational_area
        )
        .sort(
          (a, b) =>
            (a.sort_order ?? 0) - (b.sort_order ?? 0) ||
            String(a.label).localeCompare(String(b.label))
        ),
    [subProcesses, value?.operational_area]
  );
  const set = (patch) => onChange({ ...value, ...patch });
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-w-4xl"
        data-testid="recommendation-dialog"
      >
        <DialogHeader>
          <DialogTitle>
            {isEdit ? "Edit Recommendation Template" : "New Recommendation Template"}
          </DialogTitle>
          <DialogDescription>
            Consulting-grade transformation recommendation aligned with a
            specific business process, transformation theme, and rollout
            wave. Solution enablement options are listed separately and are
            never the recommendation itself.
          </DialogDescription>
        </DialogHeader>
        <div className="grid grid-cols-2 gap-4 py-2 max-h-[70vh] overflow-y-auto pr-1">
          <div className="col-span-2">
            <label className="block text-xs font-medium text-[#374151] mb-1">
              Recommendation Title
            </label>
            <Input
              value={value?.title || ""}
              onChange={(e) => set({ title: e.target.value })}
              placeholder="e.g. Standardize RF-enabled inbound receiving across the warehouse network"
              className="rounded-sm text-xs"
              data-testid="rec-field-title"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-[#374151] mb-1">
              Business Process
            </label>
            <Select
              value={value?.operational_area || ""}
              onValueChange={(v) =>
                set({ operational_area: v, sub_process_key: "" })
              }
            >
              <SelectTrigger
                className="rounded-sm text-xs"
                data-testid="rec-field-process"
              >
                <SelectValue placeholder="Select business process" />
              </SelectTrigger>
              <SelectContent>
                {processes
                  .filter((p) => p.active !== false)
                  .map((p) => (
                    <SelectItem key={p.key} value={p.key}>
                      {p.label}
                    </SelectItem>
                  ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <label className="block text-xs font-medium text-[#374151] mb-1">
              Sub-Process
            </label>
            <Select
              value={value?.sub_process_key || ""}
              onValueChange={(v) => set({ sub_process_key: v })}
              disabled={!value?.operational_area}
            >
              <SelectTrigger
                className="rounded-sm text-xs"
                data-testid="rec-field-sub-process"
              >
                <SelectValue
                  placeholder={
                    value?.operational_area
                      ? "Select sub-process (optional)"
                      : "Select business process first"
                  }
                />
              </SelectTrigger>
              <SelectContent>
                {subOptions.map((s) => (
                  <SelectItem key={s.key} value={s.key}>
                    {s.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <label className="block text-xs font-medium text-[#374151] mb-1">
              Transformation Theme
            </label>
            <Select
              value={value?.transformation_theme || ""}
              onValueChange={(v) => set({ transformation_theme: v })}
            >
              <SelectTrigger
                className="rounded-sm text-xs"
                data-testid="rec-field-theme"
              >
                <SelectValue placeholder="Select theme" />
              </SelectTrigger>
              <SelectContent>
                {TRANSFORMATION_THEMES.map((c) => (
                  <SelectItem key={c} value={c}>
                    {c}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <label className="block text-xs font-medium text-[#374151] mb-1">
              Business Impact Area
            </label>
            <Select
              value={value?.business_impact_area || ""}
              onValueChange={(v) => set({ business_impact_area: v })}
            >
              <SelectTrigger
                className="rounded-sm text-xs"
                data-testid="rec-field-impact-area"
              >
                <SelectValue placeholder="Select impact area" />
              </SelectTrigger>
              <SelectContent>
                {BUSINESS_IMPACT_AREAS.map((c) => (
                  <SelectItem key={c} value={c}>
                    {c}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <label className="block text-xs font-medium text-[#374151] mb-1">
              Rollout Wave
            </label>
            <Select
              value={value?.rollout_wave || ""}
              onValueChange={(v) =>
                set({ rollout_wave: v === "__none" ? "" : v })
              }
            >
              <SelectTrigger
                className="rounded-sm text-xs"
                data-testid="rec-field-rollout"
              >
                <SelectValue placeholder="—" />
              </SelectTrigger>
              <SelectContent>
                {ROLLOUT_WAVE.map((c) => (
                  <SelectItem
                    key={c.value || "__none"}
                    value={c.value || "__none"}
                  >
                    {c.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <label className="block text-xs font-medium text-[#374151] mb-1">
              Complexity
            </label>
            <Select
              value={value?.implementation_complexity || "medium"}
              onValueChange={(v) => set({ implementation_complexity: v })}
            >
              <SelectTrigger
                className="rounded-sm text-xs"
                data-testid="rec-field-complexity"
              >
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {COMPLEXITY.map((c) => (
                  <SelectItem key={c.value} value={c.value}>
                    {c.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <label className="block text-xs font-medium text-[#374151] mb-1">
              Warehouse Maturity Alignment
            </label>
            <Select
              value={value?.warehouse_maturity_alignment || ""}
              onValueChange={(v) =>
                set({
                  warehouse_maturity_alignment: v === "__none" ? "" : v,
                })
              }
            >
              <SelectTrigger
                className="rounded-sm text-xs"
                data-testid="rec-field-maturity"
              >
                <SelectValue placeholder="—" />
              </SelectTrigger>
              <SelectContent>
                {WAREHOUSE_MATURITY.map((c) => (
                  <SelectItem
                    key={c.value || "__none"}
                    value={c.value || "__none"}
                  >
                    {c.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="col-span-2">
            <label className="block text-xs font-medium text-[#374151] mb-1">
              Recommendation Description
            </label>
            <Textarea
              value={value?.description || ""}
              onChange={(e) => set({ description: e.target.value })}
              rows={3}
              placeholder="Executive-ready transformation narrative."
              className="rounded-sm text-xs"
              data-testid="rec-field-description"
            />
          </div>
          <div className="col-span-2">
            <label className="block text-xs font-medium text-[#374151] mb-1">
              Expected Business Impact
            </label>
            <Textarea
              value={value?.expected_business_impact || ""}
              onChange={(e) =>
                set({ expected_business_impact: e.target.value })
              }
              rows={2}
              placeholder="Quantified business impact statement."
              className="rounded-sm text-xs"
              data-testid="rec-field-expected-impact"
            />
          </div>
          <div className="col-span-2">
            <label className="block text-xs font-medium text-[#374151] mb-1">
              Operational Benefit
            </label>
            <Textarea
              value={value?.operational_benefit || ""}
              onChange={(e) => set({ operational_benefit: e.target.value })}
              rows={2}
              placeholder="What the operations team gains on the floor."
              className="rounded-sm text-xs"
              data-testid="rec-field-operational-benefit"
            />
          </div>
          <div className="col-span-2">
            <label className="block text-xs font-medium text-[#374151] mb-1">
              Executive Summary Snippet
            </label>
            <Textarea
              value={value?.executive_summary_snippet || ""}
              onChange={(e) =>
                set({ executive_summary_snippet: e.target.value })
              }
              rows={2}
              placeholder="One-line, board-ready takeaway."
              className="rounded-sm text-xs"
              data-testid="rec-field-summary"
            />
          </div>
          <div className="col-span-2">
            <label className="block text-xs font-medium text-[#374151] mb-1">
              Implementation Approach
            </label>
            <Textarea
              value={value?.implementation_approach || ""}
              onChange={(e) =>
                set({ implementation_approach: e.target.value })
              }
              rows={2}
              placeholder="Phased delivery approach for this recommendation."
              className="rounded-sm text-xs"
              data-testid="rec-field-approach"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-[#374151] mb-1">
              Duration Min (weeks)
            </label>
            <Input
              type="number"
              value={value?.duration_min_weeks ?? ""}
              onChange={(e) => set({ duration_min_weeks: e.target.value })}
              className="rounded-sm text-xs"
              data-testid="rec-field-duration-min"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-[#374151] mb-1">
              Duration Max (weeks)
            </label>
            <Input
              type="number"
              value={value?.duration_max_weeks ?? ""}
              onChange={(e) => set({ duration_max_weeks: e.target.value })}
              className="rounded-sm text-xs"
              data-testid="rec-field-duration-max"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-[#374151] mb-1">
              Functional Effort (hrs)
            </label>
            <Input
              type="number"
              value={value?.estimated_functional_effort_hours ?? ""}
              onChange={(e) =>
                set({ estimated_functional_effort_hours: e.target.value })
              }
              className="rounded-sm text-xs"
              data-testid="rec-field-functional-effort"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-[#374151] mb-1">
              Technical Effort (hrs)
            </label>
            <Input
              type="number"
              value={value?.estimated_technical_effort_hours ?? ""}
              onChange={(e) =>
                set({ estimated_technical_effort_hours: e.target.value })
              }
              className="rounded-sm text-xs"
              data-testid="rec-field-technical-effort"
            />
          </div>
          <div className="flex items-center gap-2">
            <input
              id="rec-change-management"
              type="checkbox"
              checked={!!value?.change_management_required}
              onChange={(e) =>
                set({ change_management_required: e.target.checked })
              }
              data-testid="rec-field-change-management"
            />
            <label
              htmlFor="rec-change-management"
              className="text-xs text-[#374151]"
            >
              Change management required
            </label>
          </div>
          <div className="flex items-center gap-2">
            <input
              id="rec-multi-site"
              type="checkbox"
              checked={!!value?.recommended_for_multi_site}
              onChange={(e) =>
                set({ recommended_for_multi_site: e.target.checked })
              }
              data-testid="rec-field-multi-site"
            />
            <label htmlFor="rec-multi-site" className="text-xs text-[#374151]">
              Recommended for multi-site
            </label>
          </div>
          <div className="col-span-2 pt-3 mt-2 border-t border-[#E5E7EB]">
            <p className="text-[11px] font-semibold tracking-[0.18em] uppercase text-[#025979] mb-3">
              Solution Enablement Options · Optional
            </p>
            <p className="text-[11px] text-[#6B7280] mb-3 -mt-2">
              Optional enablement mappings — the recommendation itself stays
              consulting-first.
            </p>
            <div className="space-y-3">
              <div>
                <label className="block text-xs font-medium text-[#374151] mb-2">
                  SAP Standard
                </label>
                <ChipMultiSelect
                  value={value?.sap_standard_enablement || []}
                  options={SAP_STANDARD_OPTIONS}
                  onChange={(v) => set({ sap_standard_enablement: v })}
                  testId="rec-field-sap"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-[#374151] mb-2">
                  PrimeS4 / WarehouseX Accelerators
                </label>
                <ChipMultiSelect
                  value={value?.primes4_accelerators || []}
                  options={PRIMES4_OPTIONS}
                  onChange={(v) => set({ primes4_accelerators: v })}
                  testId="rec-field-primes4"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-[#374151] mb-2">
                  Future / Advanced
                </label>
                <ChipMultiSelect
                  value={value?.future_advanced_enablement || []}
                  options={FUTURE_OPTIONS}
                  onChange={(v) => set({ future_advanced_enablement: v })}
                  testId="rec-field-future"
                />
              </div>
            </div>
          </div>
          <div className="flex items-center gap-2 pt-3 mt-2 border-t border-[#E5E7EB]">
            <input
              id="rec-active"
              type="checkbox"
              checked={value?.active !== false}
              onChange={(e) => set({ active: e.target.checked })}
              data-testid="rec-field-active"
            />
            <label htmlFor="rec-active" className="text-xs text-[#374151]">
              Active
            </label>
          </div>
          <div className="pt-3 mt-2 border-t border-[#E5E7EB]">
            <label className="block text-xs font-medium text-[#374151] mb-1">
              Sort order
            </label>
            <Input
              type="number"
              value={value?.sort_order ?? 0}
              onChange={(e) => set({ sort_order: e.target.value })}
              className="rounded-sm text-xs"
              data-testid="rec-field-sort-order"
            />
          </div>
        </div>
        <DialogFooter>
          <Button
            variant="ghost"
            onClick={() => onOpenChange(false)}
            className="rounded-sm text-xs"
            data-testid="rec-cancel"
          >
            Cancel
          </Button>
          <Button
            onClick={onSave}
            disabled={
              saving || !value?.title?.trim() || !value?.operational_area
            }
            className="bg-[#025979] hover:bg-[#03738C] text-white rounded-sm text-xs"
            data-testid="rec-save"
          >
            {saving ? "Saving…" : isEdit ? "Save changes" : "Create"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function RecommendationTemplatesPage() {
  const [rows, setRows] = useState([]);
  const [processes, setProcesses] = useState([]);
  const [subProcesses, setSubProcesses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialog, setDialog] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [filterProcess, setFilterProcess] = useState("__all");

  const reload = useCallback(async () => {
    setLoading(true);
    try {
      const [items, bps, sps] = await Promise.all([
        recommendationTemplatesApi.list(),
        operationalAreasApi.list(),
        subProcessesApi.list(),
      ]);
      setRows(items || []);
      setProcesses(bps || []);
      setSubProcesses(sps || []);
    } catch (e) {
      toast.error("Failed to load recommendation templates");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    reload();
  }, [reload]);

  const processByKey = useMemo(() => {
    const m = {};
    processes.forEach((p) => (m[p.key] = p));
    return m;
  }, [processes]);
  const subByKey = useMemo(() => {
    const m = {};
    subProcesses.forEach((s) => (m[s.key] = s));
    return m;
  }, [subProcesses]);

  const sortedRows = useMemo(() => {
    const filtered =
      filterProcess === "__all"
        ? rows
        : rows.filter((r) => r.operational_area === filterProcess);
    return [...filtered].sort(
      (a, b) =>
        (processByKey[a.operational_area]?.sort_order ?? 999) -
          (processByKey[b.operational_area]?.sort_order ?? 999) ||
        (a.sort_order ?? 0) - (b.sort_order ?? 0) ||
        String(a.title).localeCompare(String(b.title))
    );
  }, [rows, filterProcess, processByKey]);

  const openCreate = () => {
    setForm({ ...EMPTY });
    setDialog(true);
  };
  const openEdit = (row) => {
    setForm({
      id: row.id,
      slug: row.slug || "",
      title: row.title || "",
      operational_area: row.operational_area || "",
      sub_process_key: row.sub_process_key || "",
      description: row.description || "",
      transformation_theme: row.transformation_theme || "",
      business_impact_area: row.business_impact_area || "",
      implementation_complexity: row.implementation_complexity || "medium",
      rollout_wave: row.rollout_wave || "",
      warehouse_maturity_alignment: row.warehouse_maturity_alignment || "",
      expected_business_impact: row.expected_business_impact || "",
      operational_benefit: row.operational_benefit || "",
      executive_summary_snippet: row.executive_summary_snippet || "",
      implementation_approach: row.implementation_approach || "",
      duration_min_weeks: row.duration_min_weeks ?? "",
      duration_max_weeks: row.duration_max_weeks ?? "",
      estimated_functional_effort_hours:
        row.estimated_functional_effort_hours ?? "",
      estimated_technical_effort_hours:
        row.estimated_technical_effort_hours ?? "",
      change_management_required: !!row.change_management_required,
      recommended_for_multi_site: !!row.recommended_for_multi_site,
      sap_standard_enablement: row.sap_standard_enablement || [],
      primes4_accelerators: row.primes4_accelerators || [],
      future_advanced_enablement: row.future_advanced_enablement || [],
      active: row.active !== false,
      sort_order: row.sort_order ?? 0,
    });
    setDialog(true);
  };
  const toNumOrNull = (v) =>
    v === "" || v === null || v === undefined
      ? null
      : Number.isNaN(Number(v))
      ? v
      : Number(v);

  const save = async () => {
    const isEdit = !!form.id;
    const title = (form.title || "").trim();
    if (!title || !form.operational_area) return;
    const slug = isEdit ? form.slug : slugify(title);
    const payload = {
      slug,
      title,
      operational_area: form.operational_area,
      sub_process_key: form.sub_process_key || "",
      description: (form.description || "").trim(),
      transformation_theme: form.transformation_theme || "",
      business_impact_area: form.business_impact_area || "",
      implementation_complexity: form.implementation_complexity || "medium",
      rollout_wave: form.rollout_wave || "",
      warehouse_maturity_alignment: form.warehouse_maturity_alignment || "",
      expected_business_impact: (form.expected_business_impact || "").trim(),
      operational_benefit: (form.operational_benefit || "").trim(),
      executive_summary_snippet: (form.executive_summary_snippet || "").trim(),
      implementation_approach: (form.implementation_approach || "").trim(),
      duration_min_weeks: toNumOrNull(form.duration_min_weeks),
      duration_max_weeks: toNumOrNull(form.duration_max_weeks),
      estimated_functional_effort_hours: toNumOrNull(
        form.estimated_functional_effort_hours
      ),
      estimated_technical_effort_hours: toNumOrNull(
        form.estimated_technical_effort_hours
      ),
      change_management_required: !!form.change_management_required,
      recommended_for_multi_site: !!form.recommended_for_multi_site,
      sap_standard_enablement: form.sap_standard_enablement || [],
      primes4_accelerators: form.primes4_accelerators || [],
      future_advanced_enablement: form.future_advanced_enablement || [],
      active: !!form.active,
      sort_order: Number(form.sort_order) || 0,
    };
    setSaving(true);
    try {
      if (isEdit) await recommendationTemplatesApi.update(form.id, payload);
      else await recommendationTemplatesApi.create(payload);
      toast.success(
        `Recommendation template ${isEdit ? "updated" : "created"}`
      );
      setDialog(false);
      await reload();
    } catch (e) {
      const detail = e?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Failed to save");
    } finally {
      setSaving(false);
    }
  };
  const remove = async (row) => {
    if (!window.confirm(`Delete "${row.title}"?`)) return;
    try {
      await recommendationTemplatesApi.remove(row.id);
      toast.success("Recommendation template deleted");
      await reload();
    } catch (e) {
      toast.error("Failed to delete");
    }
  };

  return (
    <div className="space-y-5" data-testid="si-recommendation-templates">
      <header className="flex items-start justify-between">
        <div>
          <p className="text-[11px] font-semibold tracking-[0.18em] uppercase text-[#025979]">
            SmartImprove · Transformation Recommendation Templates
          </p>
          <h1
            className="text-2xl font-bold text-[#111827] mt-1"
            style={{ fontFamily: "Chivo, sans-serif" }}
          >
            Recommendation Engine
          </h1>
          <p className="text-sm text-[#6B7280] mt-1 max-w-3xl">
            Consulting-grade warehouse transformation recommendation
            templates aligned to operational improvement initiatives, ROI
            programs, implementation planning, and future-state enablement.
          </p>
        </div>
        <Button
          onClick={openCreate}
          className="bg-[#025979] hover:bg-[#03738C] text-white rounded-sm text-xs"
          data-testid="rec-add-btn"
        >
          <Plus className="w-3.5 h-3.5 mr-1.5" /> Add Recommendation
        </Button>
      </header>

      <div className="flex items-center gap-3">
        <label className="text-xs font-medium text-[#374151]">
          Filter by Business Process
        </label>
        <Select value={filterProcess} onValueChange={setFilterProcess}>
          <SelectTrigger
            className="rounded-sm text-xs w-72"
            data-testid="rec-filter-process"
          >
            <SelectValue placeholder="All" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="__all">All Business Processes</SelectItem>
            {processes
              .filter((p) => p.active !== false)
              .map((p) => (
                <SelectItem key={p.key} value={p.key}>
                  {p.label}
                </SelectItem>
              ))}
          </SelectContent>
        </Select>
        <span className="text-xs text-[#6B7280] ml-auto">
          {sortedRows.length} recommendation
          {sortedRows.length === 1 ? "" : "s"}
        </span>
      </div>

      <section className="bg-white border border-[#E5E7EB] rounded-sm overflow-x-auto">
        {loading ? (
          <div className="px-5 py-8 text-sm text-[#6B7280]">Loading…</div>
        ) : sortedRows.length === 0 ? (
          <div
            className="px-5 py-12 text-center text-sm text-[#6B7280]"
            data-testid="rec-empty"
          >
            No recommendation templates yet. Click{" "}
            <strong>Add Recommendation</strong> to create the first one.
          </div>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-[#F8FAFC] text-left text-[11px] uppercase tracking-wider text-[#6B7280]">
              <tr>
                <th className="px-4 py-2 font-semibold">Recommendation</th>
                <th className="px-4 py-2 font-semibold">Business Process</th>
                <th className="px-4 py-2 font-semibold">Sub-Process</th>
                <th className="px-4 py-2 font-semibold">Transformation Theme</th>
                <th className="px-4 py-2 font-semibold">Rollout Wave</th>
                <th className="px-4 py-2 font-semibold">Complexity</th>
                <th className="px-4 py-2 font-semibold">Business Impact Area</th>
                <th className="px-4 py-2 font-semibold text-right">Active</th>
                <th className="px-3 py-2" />
              </tr>
            </thead>
            <tbody>
              {sortedRows.map((row) => {
                const bp = processByKey[row.operational_area];
                const sp = subByKey[row.sub_process_key];
                const complexity = String(
                  row.implementation_complexity || ""
                ).toLowerCase();
                const wave = row.rollout_wave || "";
                return (
                  <tr
                    key={row.id}
                    className="border-t border-[#F1F5F9] hover:bg-[#F8FAFC] align-top"
                    data-testid={`rec-row-${row.id}`}
                  >
                    <td className="px-4 py-3">
                      <div className="text-[#111827] font-medium">
                        {row.title}
                      </div>
                      {row.executive_summary_snippet || row.description ? (
                        <p className="text-[11px] text-[#6B7280] mt-0.5 line-clamp-2 max-w-md">
                          {row.executive_summary_snippet || row.description}
                        </p>
                      ) : null}
                    </td>
                    <td className="px-4 py-3 text-[#374151]">
                      {bp?.label || <span className="text-[#9CA3AF]">—</span>}
                    </td>
                    <td className="px-4 py-3 text-[#374151]">
                      {sp?.label || <span className="text-[#9CA3AF]">—</span>}
                    </td>
                    <td className="px-4 py-3 text-[#374151]">
                      {row.transformation_theme || (
                        <span className="text-[#9CA3AF]">—</span>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      {wave ? (
                        <span
                          className={`inline-flex items-center px-2 py-0.5 text-[11px] font-semibold border rounded-sm ${
                            WAVE_PILL[wave] ||
                            "bg-[#F3F4F6] text-[#374151] border-[#E5E7EB]"
                          }`}
                        >
                          {WAVE_SHORT_LABEL[wave] || wave}
                        </span>
                      ) : (
                        <span className="text-[#9CA3AF]">—</span>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      {complexity ? (
                        <span
                          className={`inline-flex items-center px-2 py-0.5 text-[11px] font-semibold border rounded-sm capitalize ${
                            COMPLEXITY_PILL[complexity] ||
                            "bg-[#F3F4F6] text-[#374151] border-[#E5E7EB]"
                          }`}
                        >
                          {complexity}
                        </span>
                      ) : (
                        <span className="text-[#9CA3AF]">—</span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-[#374151]">
                      {row.business_impact_area || (
                        <span className="text-[#9CA3AF]">—</span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-right">
                      {row.active === false ? (
                        <span className="inline-flex items-center px-2 py-0.5 text-[11px] font-semibold border rounded-sm bg-[#F3F4F6] text-[#6B7280] border-[#E5E7EB]">
                          inactive
                        </span>
                      ) : (
                        <span className="inline-flex items-center px-2 py-0.5 text-[11px] font-semibold border rounded-sm bg-[#ECFDF5] text-[#065F46] border-[#A7F3D0]">
                          active
                        </span>
                      )}
                    </td>
                    <td className="px-3 py-3 text-right whitespace-nowrap">
                      <button
                        onClick={() => openEdit(row)}
                        className="text-xs font-semibold text-[#025979] hover:text-[#03738C] mr-3"
                        data-testid={`rec-edit-${row.id}`}
                        aria-label={`Edit ${row.title}`}
                      >
                        <Pencil className="inline w-3.5 h-3.5" />
                      </button>
                      <button
                        onClick={() => remove(row)}
                        className="text-xs font-semibold text-[#B91C1C] hover:text-[#7F1D1D]"
                        data-testid={`rec-delete-${row.id}`}
                        aria-label={`Delete ${row.title}`}
                      >
                        <Trash2 className="inline w-3.5 h-3.5" />
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </section>

      <RecommendationDialog
        open={dialog}
        onOpenChange={setDialog}
        value={form}
        processes={processes}
        subProcesses={subProcesses}
        onChange={setForm}
        onSave={save}
        saving={saving}
      />
    </div>
  );
}
